import React from 'react'
import { Link, useLocation } from 'react-router'
import { Anchor } from '@mantine/core'
import {
  IconAward,
  IconBolt,
  IconHelmet,
  IconHome,
  IconListNumbers,
  IconScoreboard,
  IconUsers,
} from '@tabler/icons-react'
import classes from './Navigation.module.css'
import clsx from 'clsx'
import { ReactComponent as FabButton } from '../assets/FabButton.svg'
import { ReactComponent as Clair } from '../assets/clair.svg'
import { useIsPlayer } from '../common/api'
import { useActiveCompetitions, useCompetitionDetails } from './api'

declare global {
  interface Navigator {
    standalone?: boolean
  }
}

interface MenuItemProps {
  icon: React.ComponentType<{ className?: string }>
  label: string
  link?: string
  onClick?: () => void
  disabled?: boolean
}

function isPWA(): boolean {
  const isStandaloneMode = window.matchMedia(
    '(display-mode: standalone)'
  ).matches

  const isIOSStandalone = window.navigator.standalone === true

  return isStandaloneMode || isIOSStandalone
}

function isIOS(): boolean {
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  )
}

function MenuItem({
  icon: Icon,
  label,
  link,
  onClick,
  disabled,
}: MenuItemProps) {
  const location = useLocation()
  const isActive =
    !disabled &&
    link &&
    location.pathname.toLowerCase().startsWith(link.toLowerCase())

  const content = (
    <>
      <Icon className={clsx(classes.icon, { [classes.active]: isActive })} />
      {label}
    </>
  )

  if (disabled) {
    return (
      <div className={clsx(classes.mobileLink, classes.disabled)}>
        {content}
      </div>
    )
  }

  if (!link) {
    return (
      <div
        className={clsx(classes.mobileLink, { [classes.active]: isActive })}
        onClick={onClick}
      >
        {content}
      </div>
    )
  }

  return (
    <Anchor
      component={Link}
      to={link}
      underline="never"
      className={clsx(classes.mobileLink, { [classes.active]: isActive })}
      onClick={onClick}
    >
      {content}
    </Anchor>
  )
}

interface Props {
  setMobileNavigationOpen: React.Dispatch<React.SetStateAction<boolean>>
}

export default function MobileNavigation({ setMobileNavigationOpen }: Props) {
  const { team_id, competition_id } = useIsPlayer()
  const competitionDetails = useCompetitionDetails({
    competition_id: competition_id,
  })
  const { data: activeCompetitionsData } = useActiveCompetitions()
  const platformFeatures = activeCompetitionsData?.platform_features

  const hasArena = platformFeatures?.has_arena ?? true
  const hasLearning = platformFeatures?.has_learning_module ?? true
  const hasClair = platformFeatures?.has_clair_ai ?? true

  const isRace = competitionDetails.data?.kind === 'race'

  return (
    <div className={classes.mobileFooterContainer}>
      <div
        className={clsx(classes.footerButtonContainer, {
          [classes.footerButtonContainerPWA]: isPWA(),
        })}
      >
        <FabButton
          className={classes.fabButton}
          onClick={() => setMobileNavigationOpen(true)}
        />
      </div>
      <div
        className={clsx(classes.mobileNavigation, {
          [classes.pwaMobileNavigation]: isPWA() || isIOS(),
        })}
      >
        <MenuItem
          icon={IconHome}
          label="Home"
          link={`/competitions/${competition_id}/home`}
        />
        {hasArena ? (
          <MenuItem
            icon={IconAward}
            label="Arena"
            link={`/competitions/${competition_id}/arena`}
          />
        ) : hasLearning ? (
          <MenuItem icon={IconBolt} label="Learning" link="/learning" />
        ) : hasClair ? (
          <MenuItem icon={Clair} label="Clair" link="/clair" />
        ) : isRace ? (
          <MenuItem
            icon={IconHelmet}
            label="Races"
            link={`/competitions/${competition_id}/races`}
          />
        ) : (
          <MenuItem
            icon={IconScoreboard}
            label="Matches"
            link={`/competitions/${competition_id}/matches`}
          />
        )}
        <div className={classes.navSpacer} />
        <MenuItem
          icon={IconListNumbers}
          label="League"
          link={`/competitions/${competition_id}/ladder`}
        />
        {team_id ? (
          <MenuItem
            icon={IconUsers}
            label="My Team"
            link={`/competitions/${competition_id}/teams/${team_id}/overview`}
          />
        ) : (
          <MenuItem icon={IconUsers} label="My Team" disabled />
        )}
      </div>
    </div>
  )
}
